import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { LazyRichTextField } from '../../../../components/inputs/LazyRichTextField';
import { TextField } from '../../../../components/inputs/TextField';
import { Modal } from '../../../../components/Modal';
import { Stepper } from '../../../../components/Stepper';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { competenciesApi } from '../../../human-resources/competencies/competencyApi';
import type { CompetencyLevel, CompetencyOption } from '../../../human-resources/personnel/personnelTypes';
import { surveyPlansApi } from '../surveyApi';
import type { SurveyPlanDetail } from '../surveyTypes';
import { blankNeed, isBlankNeed, needProblem, type DraftNeed } from '../utils/developmentNeeds';
import {
  sampleDevelopmentNeeds,
  samplePlanDocument,
  samplePlanTitle,
  sampleTrainingRecommendationText,
  sampleTrainingTitle,
} from '../utils/sampleDevelopmentPlan';
import { DevelopmentNeedsGrid } from './DevelopmentNeedsGrid';
import { blankTraining, isBlankTraining, isUntitledTraining, toTrainingRequest, type DraftTraining } from '../utils/trainingRecommendationRows';
import { PlanEmployeePicker, type PlanEmployee } from './PlanEmployeePicker';
import { TrainingRecommendationsGrid } from './TrainingRecommendationsGrid';

const ID = 'survey-plan';

const STEPS = [
  { key: 'plan', label: 'Development Plan' },
  { key: 'training', label: 'Training Recommendation' },
  { key: 'employees', label: 'Employees' },
] as const;

const TRAINING_STEP = 1;
const EMPLOYEES_STEP = 2;

/**
 * Survey › Development Plan › Create — ONE plan for several assessed employees (product call,
 * 12 Oct 2026). A stepper:
 *
 * 1. **Development Plan** — its Title, its ONE Identified Development Need (the employee plan
 *    form's own fields) and the plan document.
 * 2. **Training Recommendation** — an inline grid, a row per recommended training (its title and
 *    what is recommended), optional as a whole. Each becomes a row of the plan's Training
 *    Recommendations, where more are added and each is approved on its own.
 * 3. **Employees** — who it is for: ANY employee, searched and added to a table (product call,
 *    13 Oct 2026) — assessed or not, and whether or not they answered the survey. One who did not
 *    answer is on the plan without counting as a response. An employee who already has a plan in
 *    this survey is refused by the server, naming their row.
 *
 * Submit writes the plan and gives EACH chosen employee their own copy, all or none — the confirm
 * tick is on the last step only. Writing takes `surveys.plan`.
 *
 * Autogenerate fills every step: the title, the need, the plan document, two recommendation rows,
 * and the first three employees the search has loaded. The editor is uncontrolled after mount, so
 * its sample is shown by REMOUNTING it under a bumped `key` (product call, 13 Oct 2026).
 */
export function SurveyPlanCreateModal({
  open,
  surveyId,
  onClose,
  onCreated,
}: {
  open: boolean;
  surveyId: string;
  onClose: () => void;
  onCreated: (created: SurveyPlanDetail) => void;
}) {
  const toast = useToast();

  const [competencies, setCompetencies] = useState<CompetencyOption[]>([]);

  const [step, setStep] = useState(0);
  const [title, setTitle] = useState('');
  // One need, and its fields are on the form from the start; left blank, it is simply left out.
  const [needs, setNeeds] = useState<DraftNeed[]>(() => [blankNeed()]);
  const [plan, setPlan] = useState('');
  // The grid is never empty: it opens on one blank row, and a blank row is dropped on save.
  const [trainings, setTrainings] = useState<DraftTraining[]>(() => [blankTraining()]);
  const [people, setPeople] = useState<PlanEmployee[]>([]);
  // What the employee search has loaded — Autogenerate picks from it.
  const [offered, setOffered] = useState<PlanEmployee[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  // Bumped by Autogenerate: the editor takes its value ONCE, so a sample shows only in a fresh editor.
  const [editorKey, setEditorKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    // The framework, for the needs' picker — through `surveys.plan`'s implied read.
    competenciesApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setCompetencies(next);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [surveyId]);

  const isLastStep = step === STEPS.length - 1;
  const needsValid = needs.every((n) => needProblem(n) === null);
  const planValid = title.trim() !== '' && needsValid;

  // The recommendations are optional as a whole — but a recommendation with no title is one
  // nobody could find in its table.
  const trainingValid = !trainings.some(isUntitledTraining);

  // Nobody ticked is allowed (product call, 12 Oct 2026): the plan saves with no employee on it yet.
  const canSave = planValid && trainingValid && isLastStep && confirmed && !saving;

  const competencyOptions = () =>
    competencies.map((c) => ({ value: c.id, label: c.name, sublabel: c.category ?? undefined }));

  function generate() {
    setTitle(samplePlanTitle());
    setTrainings([0, 1].map(() => ({ key: uid(), title: sampleTrainingTitle(), details: sampleTrainingRecommendationText() })));
    setPlan(samplePlanDocument());
    setEditorKey((k) => k + 1);
    setNeeds(sampleDevelopmentNeeds(competencies.map((c) => c.id)).map((n) => ({ key: uid(), id: null, ...n })));
    setPeople(offered.slice(0, 3));
  }

  async function save() {
    if (!canSave) return;

    const sentTrainings = trainings.filter((t) => !isBlankTraining(t));

    setSaving(true);
    setError(null);

    try {
      const created = await surveyPlansApi.create(surveyId, {
        title: title.trim(),
        plan,
        // Optional as a whole: a blank row is left out. A cell holds plain text; the column holds a document.
        trainingRecommendations: sentTrainings.map(toTrainingRequest),
        needs: needs
          .filter((n) => !isBlankNeed(n))
          .map((n) => ({
            id: null,
            competencyId: n.competencyId || null,
            currentLevel: n.currentLevel === '' ? null : (Number(n.currentLevel) as CompetencyLevel),
            targetLevel: n.targetLevel === '' ? null : (Number(n.targetLevel) as CompetencyLevel),
            gap: n.gap.trim() || null,
          })),
        respondentIds: [],
        personnelIds: people.map((e) => e.id),
      });

      const count = created.plan.members.length;

      toast.success(
        count === 0
          ? `Created "${created.plan.title}" with no employee on it yet.`
          : `Created "${created.plan.title}" for ${count} ${count === 1 ? 'employee' : 'employees'}.`,
      );
      onCreated(created);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('The development plan could not be created.', 0);

      setError(failure);

      // A refusal names the title, a need's row, or the employees — go to that step, then focus.
      const fieldIds: Record<string, string> = {
        Title: `${ID}-title`,
        title: `${ID}-title`,
        PersonnelIds: `${ID}-personnel-ids`,
        personnelIds: `${ID}-personnel-ids`,
      };

      // A refusal names a recommendation by its row AS SENT — map that back to the row on screen.
      sentTrainings.forEach((t, sentIndex) => {
        const row = trainings.indexOf(t);

        for (const [field, cell] of [
          ['Title', 'title'],
          ['Details', 'details'],
        ] as const) {
          fieldIds[`TrainingRecommendations[${sentIndex}].${field}`] = `${ID}-training-recommendations.${row}.${cell}`;
          fieldIds[`trainingRecommendations[${sentIndex}].${cell}`] = `${ID}-training-recommendations.${row}.${cell}`;
        }
      });

      // A refusal names an employee by their ROW; the one picker stands for all of them.
      people.forEach((_, i) => {
        fieldIds[`PersonnelIds[${i}]`] = `${ID}-personnel-ids`;
        fieldIds[`personnelIds[${i}]`] = `${ID}-personnel-ids`;
      });

      needs
        .map((n, i) => ({ n, i }))
        .filter(({ n }) => !isBlankNeed(n))
        .forEach(({ i }, sentIndex) => {
          for (const [field, cell] of [
            ['CompetencyId', 'competencyId'],
            ['CurrentLevel', 'currentLevel'],
            ['TargetLevel', 'targetLevel'],
            ['Gap', 'gap'],
          ] as const) {
            fieldIds[`Needs[${sentIndex}].${field}`] = `${ID}-needs.${i}.${cell}`;
            fieldIds[`needs[${sentIndex}].${field[0].toLowerCase()}${field.slice(1)}`] = `${ID}-needs.${i}.${cell}`;
          }
        });

      const named = Object.keys(failure.fields ?? {}).map((k) => k.toLowerCase());

      if (named.length > 0) {
        setStep(
          named.some((k) => k.startsWith('respondentids') || k.startsWith('personnelids'))
            ? EMPLOYEES_STEP
            : named.some((k) => k.startsWith('trainingrecommendation'))
              ? TRAINING_STEP
              : 0,
        );
        window.requestAnimationFrame(() =>
          window.requestAnimationFrame(() => focusValidationError(failure, { idPrefix: ID, fieldIds })),
        );
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New development plan"
      // Sized to the Needs grid, as the employee's own plan form is.
      size="2xl"
      id={`${ID}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving || competencies.length === 0 }}
      // The tick on the LAST step only — its appearing says nothing is left to fill in.
      confirm={
        isLastStep ? { id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving } : undefined
      }
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          {/* Back never validates — going back is how a step gets fixed. */}
          {step > 0 ? (
            <Button id={`${ID}-back`} variant="secondary" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={saving}>
              Back
            </Button>
          ) : null}

          {!isLastStep ? (
            <Button id={`${ID}-next`} variant="secondary" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} disabled={!planValid || (step === TRAINING_STEP && !trainingValid)}>
              Next
            </Button>
          ) : null}

          <Button
            id={`${ID}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Stepper
          id={ID}
          steps={STEPS.map(({ key, label }) => ({ key, label }))}
          activeIndex={step}
          onJump={(index) => {
            // Back is free; forward only past a first step that holds.
            if (index <= step || (planValid && (index <= TRAINING_STEP || trainingValid))) setStep(index);
          }}
        />

        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {step === 0 ? (
          <>
            {/*
              The three section labels are BOLD (product call, 12 Oct 2026) — Title, Identified
              Development Need, Development Plan. Scoped from here, by wrapper, because the shared
              inputs draw their labels at regular weight for every other form; the need's own
              field labels inside the fieldset are deliberately not reached.
            */}
            <div className="[&_label]:font-semibold">
              <TextField
                id={`${ID}-title`}
                label="Title"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                error={error?.fieldError('title') ?? (title.trim() === '' ? 'Enter a title.' : undefined)}
                disabled={saving}
                maxLength={300}
                autoFocus
              />
            </div>

            <div className="[&>fieldset>legend]:font-semibold">
            <FormFieldset legend="Identified Development Need">
              <DevelopmentNeedsGrid
                idPrefix={ID}
                needs={needs}
                onChange={setNeeds}
                competencyOptions={competencyOptions}
                disabled={saving}
              />
            </FormFieldset>
            </div>

            <div className="[&_label]:font-semibold">
              <LazyRichTextField
                key={editorKey}
                id={`${ID}-plan`}
                label="Development Plan"
                value={plan}
                onChange={setPlan}
                maxLength={8000}
                disabled={saving}
              />
            </div>
          </>
        ) : step === TRAINING_STEP ? (
          /*
            THE TRAINING RECOMMENDATIONS — an inline-add grid (product call, 13 Oct 2026; it was one
            title over a rich-text editor): a row per recommendation, its title and what is
            recommended. Optional as a whole — the blank row the grid always holds is dropped on
            save — and more are added from the plan's profile, each with its own approval. No new
            row while one is still blank.
          */
          <TrainingRecommendationsGrid idPrefix={ID} rows={trainings} onChange={setTrainings} disabled={saving} />
        ) : (
          /*
            ANY employee (product call, 13 Oct 2026) — assessed or not, and whether or not they
            answered the survey. Nobody at all is allowed too: the plan saves with no employee yet.
          */
          <PlanEmployeePicker
            idPrefix={ID}
            value={people}
            onChange={setPeople}
            onOffered={setOffered}
            disabled={saving}
            error={error?.fieldError('personnelIds')}
          />
        )}
      </div>
    </Modal>
  );
}
